import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Lobby from "@/pages/Lobby";
import Contest from "@/pages/Contest";
import Dashboard from "@/pages/Dashboard";
import Entries from "@/pages/Entries";
import Wallet from "@/pages/Wallet";
import Friends from "@/pages/Friends";
import CreateChallenge from "@/pages/CreateChallenge";
import JoinChallenge from "@/pages/JoinChallenge";
import Manager from "@/pages/Manager";
import ManagerAgreement from "@/pages/ManagerAgreement";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

// Lazy-load heavier routes (SEO warned that these were slow to load).
// Legal ships react-simple-maps + topojson; Merch ships product imagery.
// Admin is auth-only, no reason to ship it on the marketing bundle either.
const Legal = lazy(() => import("@/pages/Legal"));
const StatePage = lazy(() => import("@/pages/StatePage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Merch = lazy(() => import("@/pages/Merch"));
const Admin = lazy(() => import("@/pages/Admin"));
const Contact = lazy(() => import("@/pages/Contact"));
const Managers = lazy(() => import("@/pages/Managers"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white/60">
    <div className="font-display text-3xl tracking-widest animate-pulse">LOADING...</div>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white/60">
        <div className="font-display text-3xl tracking-widest animate-pulse">LOADING...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/:state" element={<StatePage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/merch" element={<Merch />} />
        <Route path="/managers" element={<Managers />} />
        <Route path="/join/:code" element={<JoinChallenge />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/lobby" element={<ProtectedRoute><Lobby /></ProtectedRoute>} />
        <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
        <Route path="/friends/new" element={<ProtectedRoute><CreateChallenge /></ProtectedRoute>} />
        <Route path="/manager" element={<ProtectedRoute><Manager /></ProtectedRoute>} />
        <Route path="/manager/agreement" element={<ProtectedRoute><ManagerAgreement /></ProtectedRoute>} />
        <Route path="/contest/:id" element={<ProtectedRoute><Contest /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/entries" element={<ProtectedRoute><Entries /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#141414",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
